// Opens the live-update stream (GET /api/events) and keeps it open.
//
// The stream is opened with a one-time ticket (POST /api/events/ticket, see
// server/sseTickets.js) rather than the login JWT in the URL, which would
// land in the proxy's access log. A used ticket can't be reused, so
// EventSource's own auto-reconnect (same URL) would fail — instead, on any
// error this closes the connection and opens a new one with a fresh ticket,
// backing off 1s → 2s → … → 30s while the server is unreachable. If the
// server predates tickets (404), it falls back to the old ?token= URL and
// EventSource's own reconnects, exactly as before.
//
// handlers: { [eventName]: (event) => void }, attached to every connection
// ('open' fires on connect and on every reconnect).
// Returns a function that closes the stream for good.
export const openLiveConnection = ({ token, handlers, fetchImpl = fetch, EventSourceImpl = EventSource }) => {
  let source = null;
  let cancelled = false;
  let retryTimer = null;
  let retryDelay = 1000;

  const closeSource = () => {
    if (!source) return;
    for (const [name, handler] of Object.entries(handlers)) source.removeEventListener(name, handler);
    source.close();
    source = null;
  };

  const scheduleReconnect = () => {
    if (cancelled || retryTimer) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 30000);
  };

  const openSource = (url, { reconnectWithNewTicket }) => {
    source = new EventSourceImpl(url);
    for (const [name, handler] of Object.entries(handlers)) source.addEventListener(name, handler);
    source.addEventListener('open', () => {
      retryDelay = 1000;
    });
    if (reconnectWithNewTicket) {
      source.addEventListener('error', () => {
        closeSource();
        scheduleReconnect();
      });
    }
  };

  const connect = async () => {
    let response;
    try {
      response = await fetchImpl('/api/events/ticket', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      // Server unreachable (e.g. restarting) — try again shortly.
      scheduleReconnect();
      return;
    }
    if (cancelled) return;
    if (response.status === 404) {
      openSource(`/api/events?token=${encodeURIComponent(token)}`, { reconnectWithNewTicket: false });
      return;
    }
    // An invalid/expired session: the old ?token= connection simply failed
    // here too. authFetch's 401 handling elsewhere logs the user out.
    if (response.status === 401) return;
    let ticket = null;
    try {
      if (response.ok) ticket = (await response.json()).ticket;
    } catch (error) {
      ticket = null;
    }
    if (cancelled) return;
    if (!ticket) {
      scheduleReconnect();
      return;
    }
    openSource(`/api/events?ticket=${encodeURIComponent(ticket)}`, { reconnectWithNewTicket: true });
  };

  connect();

  return () => {
    cancelled = true;
    clearTimeout(retryTimer);
    closeSource();
  };
};
